import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { duration } from './primitives/tokens';
import { cva } from 'class-variance-authority';
import styles from './Spin.module.css';

const spinnerContentCva = cva(styles.spinnerContent, {
  variants: {
    withTip: { true: styles.withTip },
  },
});

const contentWrapperCva = cva(styles.contentWrapper, {
  variants: {
    spinning: {
      true: styles.spinning,
      false: styles.notSpinning,
    },
  },
});

type SpinSize = 'sm' | 'md' | 'lg';

type DivMotionSafeProps = Omit<React.HTMLAttributes<HTMLDivElement>, 'onDrag' | 'onDragEnd' | 'onDragStart' | 'onAnimationStart'>;

interface SpinProps extends DivMotionSafeProps {
  /** Whether to show the spinner */
  spinning?: boolean;
  /** Spinner size */
  size?: SpinSize;
  /** Text below the spinner */
  tip?: string;
  /** Delay before showing spinner (ms) — prevents flash for fast loads */
  delay?: number;
  /** Custom spinner element */
  indicator?: React.ReactNode;
  /** Content to wrap — shows dimmed with spinner overlay when spinning */
  children?: React.ReactNode;
}

const spinSizes: Record<SpinSize, number> = {
  sm: 16,
  md: 24,
  lg: 40,
};

/**
 * Spin — loading indicator with optional content overlay.
 *
 * | Prop | Type | Default | Description |
 * |------|------|---------|-------------|
 * | spinning | boolean | true | Show/hide the spinner |
 * | size | 'sm' \| 'md' \| 'lg' | 'md' | Spinner diameter |
 * | tip | string | — | Text label below spinner |
 * | delay | number | — | Delay (ms) before showing |
 * | indicator | ReactNode | — | Custom spinner element |
 * | children | ReactNode | — | Content to overlay with spinner |
 *
 * @example
 * <Spin />
 * <Spin tip="Loading..." size="lg" />
 * <Spin spinning={isLoading}>
 *   <Card>Content here</Card>
 * </Spin>
 */
export function Spin({ spinning = true, size = 'md', tip, delay, indicator, children, style, ...props }: SpinProps) {
  const safeProps = props;
  const [visible, setVisible] = useState(delay ? false : spinning);

  useEffect(() => {
    if (!delay) {
      setVisible(spinning);
      return;
    }
    if (spinning) {
      const timer = setTimeout(() => setVisible(true), delay);
      return () => clearTimeout(timer);
    }
    setVisible(false);
  }, [spinning, delay]);

  const diameter = spinSizes[size];

  const defaultIndicator = (
    <div
      className={styles.defaultIndicator}
      style={{
        '--spin-diameter': `${diameter}px`,
      } as React.CSSProperties}
    />
  );

  const spinnerContentElement = (
    <div className={spinnerContentCva({ withTip: !!tip })}>
      {indicator ?? defaultIndicator}
      {tip && (
        <span className={styles.tipText}>
          {tip}
        </span>
      )}
    </div>
  );

  // Standalone spinner (no children)
  if (!children) {
    return (
      <AnimatePresence>
        {visible && (
          <motion.div
            className={styles.standaloneWrapper}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: duration.normal / 1000 }}
            style={style}
            {...safeProps}
          >
            {spinnerContentElement}
          </motion.div>
        )}
      </AnimatePresence>
    );
  }

  // Wrapper mode — overlay spinner on content
  return (
    <div className={styles.wrapperRoot} style={style} {...safeProps}>
      <AnimatePresence>
        {visible && (
          <motion.div
            className={styles.overlayDiv}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: duration.normal / 1000 }}
          >
            {spinnerContentElement}
          </motion.div>
        )}
      </AnimatePresence>
      <div className={contentWrapperCva({ spinning: visible })}>
        {children}
      </div>
    </div>
  );
}
